import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { SeverityTier } from "../types/severity-tier";

const SEVERITY_VALUES = new Set(Object.values(SeverityTier));

function parseSeverityTiers(raw: string): SeverityTier[] {
  if (!raw) return [];
  return raw
    .split(",")
    .filter((v): v is SeverityTier => SEVERITY_VALUES.has(v as SeverityTier));
}

function parseMultiValueParam(searchParams: URLSearchParams, key: string): string[] {
  const allValues = searchParams
    .getAll(key)
    .flatMap((value) => value.split(","))
    .filter((value) => value !== "");
  return [...new Set(allValues)];
}

function parseIntParam(raw: string | null, fallback: number): number {
  if (!raw) return fallback;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

export interface PacketListParams {
  orgCategory: string;
  orgId: string;
  page: number;
  pageSize: number;
  severityTiers: SeverityTier[];
  component: string;
  payGroups: string[];
  payPeriodEnds: string[];
  setPage: (n: number) => void;
  setPageSize: (n: number) => void;
  setSeverityTiers: (tiers: SeverityTier[]) => void;
  setComponent: (component: string) => void;
  setPayGroups: (payGroups: string[]) => void;
  setPayPeriodEnds: (payPeriodEnds: string[]) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
}

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 25;

export function usePacketListParams(): PacketListParams {
  const [searchParams, setSearchParams] = useSearchParams();

  const orgCategory = searchParams.get("org_category") ?? "";
  const orgId = searchParams.get("org_id") ?? "";
  const page = parseIntParam(searchParams.get("page"), DEFAULT_PAGE);
  const pageSize = parseIntParam(
    searchParams.get("page_size"),
    DEFAULT_PAGE_SIZE,
  );
  const severityTiers = useMemo(
    () => parseSeverityTiers(searchParams.get("severity") ?? ""),
    [searchParams],
  );
  const component = searchParams.get("component") ?? "";
  const payGroups = useMemo(
    () => parseMultiValueParam(searchParams, "pay_group"),
    [searchParams],
  );
  const payPeriodEnds = useMemo(
    () => parseMultiValueParam(searchParams, "pay_period_end"),
    [searchParams],
  );

  const setPage = useCallback(
    (n: number) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (n <= 1) {
          next.delete("page");
        } else {
          next.set("page", String(n));
        }
        return next;
      });
    },
    [setSearchParams],
  );

  const setPageSize = useCallback(
    (n: number) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("page_size", String(n));
        next.delete("page");
        return next;
      });
    },
    [setSearchParams],
  );

  const setSeverityTiers = useCallback(
    (tiers: SeverityTier[]) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (tiers.length === 0) {
          next.delete("severity");
        } else {
          next.set("severity", tiers.join(","));
        }
        next.delete("page");
        return next;
      });
    },
    [setSearchParams],
  );

  const setComponent = useCallback(
    (c: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (c) {
          next.set("component", c);
        } else {
          next.delete("component");
        }
        next.delete("page");
        return next;
      });
    },
    [setSearchParams],
  );

  const setPayGroups = useCallback(
    (groups: string[]) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("pay_group");
        for (const group of groups) {
          next.append("pay_group", group);
        }
        next.delete("page");
        return next;
      });
    },
    [setSearchParams],
  );

  const setPayPeriodEnds = useCallback(
    (periodEnds: string[]) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("pay_period_end");
        for (const periodEnd of periodEnds) {
          next.append("pay_period_end", periodEnd);
        }
        next.delete("page");
        return next;
      });
    },
    [setSearchParams],
  );

  const clearFilters = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("severity");
      next.delete("component");
      next.delete("pay_group");
      next.delete("pay_period_end");
      next.delete("page");
      return next;
    });
  }, [setSearchParams]);

  const hasActiveFilters =
    severityTiers.length > 0 ||
    component !== "" ||
    payGroups.length > 0 ||
    payPeriodEnds.length > 0;

  return {
    orgCategory,
    orgId,
    page,
    pageSize,
    severityTiers,
    component,
    payGroups,
    payPeriodEnds,
    setPage,
    setPageSize,
    setSeverityTiers,
    setComponent,
    setPayGroups,
    setPayPeriodEnds,
    clearFilters,
    hasActiveFilters,
  };
}
